import React, { useState, useEffect } from 'react';
import { AlertTriangle, Siren, ShieldCheck, X, Navigation, Phone, Map, Copy, MessageCircle } from 'lucide-react';
import { View } from '../types';

interface AlertData {
    title?: string;
    body?: string;
    level?: string; // '1' | '2' | '5'
    type?: string;
    pet_id?: string;
    pet_name?: string;
    pet_photo?: string;
    lat?: string;
    lng?: string;
    reporter_name?: string;
    sighting_id?: string;
    alert_id?: string;
}

export const SystemAlertOverlay: React.FC = () => {
    const [alert, setAlert] = useState<AlertData | null>(null);

    useEffect(() => {
        const handleAlert = (event: any) => {
            console.log("System Alert Received:", event.detail);
            const data = event.detail;
            if (data && (data.level || data.type === 'system_alert')) {
                setAlert(data);
                playAlertSound(data.level);
            }
        };

        window.addEventListener('system-alert' as any, handleAlert);
        return () => window.removeEventListener('system-alert' as any, handleAlert);
    }, []);

    const playAlertSound = (levelStr?: string) => {
        const level = parseInt(levelStr || '1');
        const AudioContext = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (AudioContext) {
            const ctx = new AudioContext();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);

            if (level === 5) {
                osc.type = 'sawtooth';
                osc.frequency.setValueAtTime(800, ctx.currentTime);
                osc.frequency.linearRampToValueAtTime(1200, ctx.currentTime + 0.5);
                osc.frequency.linearRampToValueAtTime(800, ctx.currentTime + 1.0);
                osc.frequency.linearRampToValueAtTime(1200, ctx.currentTime + 1.5);
                gain.gain.setValueAtTime(0.5, ctx.currentTime);
                osc.start();
                osc.stop(ctx.currentTime + 3);
            } else if (level === 2) {
                osc.type = 'square';
                osc.frequency.setValueAtTime(600, ctx.currentTime);
                osc.frequency.setValueAtTime(800, ctx.currentTime + 0.2);
                gain.gain.setValueAtTime(0.3, ctx.currentTime);
                osc.start();
                osc.stop(ctx.currentTime + 1);
            } else {
                osc.type = 'sine';
                osc.frequency.setValueAtTime(440, ctx.currentTime);
                gain.gain.setValueAtTime(0.2, ctx.currentTime);
                osc.start();
                osc.stop(ctx.currentTime + 0.5);
            }
        }

        if ('speechSynthesis' in window) {
            const msg = new SpeechSynthesisUtterance(
                level === 5 ? "¡ALERTA MÁXIMA! ¡MASCOTA PERDIDA EN SU ZONA! POR FAVOR REVISE SU APLICACIÓN." :
                    level === 2 ? "Atención: Tienes un recordatorio médico urgente." :
                        "Aviso: Tienes un recordatorio pendiente."
            );
            msg.rate = 1.1;
            window.speechSynthesis.speak(msg);
        }
    };

    const closeAlert = () => {
        setAlert(null);
        if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    };

    if (!alert) return null;

    const level = parseInt(alert.level || '1');

    // LEVEL 5: RESCUE CARD
    if (level === 5) {
        return (
            <div className="fixed inset-0 z-[100000] bg-slate-900 flex flex-col items-center justify-center animate-in zoom-in duration-300 text-white p-6 overflow-y-auto">
                <div className="absolute inset-0 bg-gradient-to-b from-red-600/50 to-slate-900 z-0"></div>
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20 pointer-events-none"></div>

                <div className="relative z-10 w-full max-w-sm flex flex-col items-center">
                    {alert.type === 'system_alert' ? (
                        /* QR Scan — alguien encontró la mascota */
                        <div className="mb-4 flex flex-col items-center">
                            <div className="w-20 h-20 rounded-[2rem] bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center mb-3 shadow-lg shadow-emerald-500/20 animate-bounce">
                                <ShieldCheck size={40} className="text-emerald-400" />
                            </div>
                            <h2 className="text-3xl font-black tracking-tighter text-white uppercase italic text-center">¡Placa Escaneada!</h2>
                            <p className="text-emerald-400 font-black text-xs uppercase tracking-widest mt-1">Alguien encontró a tu mascota</p>
                            <div className="h-1 w-24 bg-emerald-500 rounded-full mt-2"></div>
                        </div>
                    ) : (
                        /* Alerta estándar perdido */
                        <div className="animate-pulse mb-4 flex flex-col items-center">
                            <Siren size={64} className="text-red-500 mb-2" />
                            <h2 className="text-3xl font-black tracking-tighter text-white uppercase italic">¡Está Perdido!</h2>
                            <div className="h-1 w-24 bg-red-600 rounded-full mt-2"></div>
                        </div>
                    )}

                    <div className={`bg-white rounded-[2.5rem] overflow-hidden shadow-2xl w-full mb-6 border-4 ${alert.type === 'system_alert' ? 'border-emerald-500' : 'border-red-600'}`}>
                        <div className="h-64 bg-slate-200 relative">
                            {alert.pet_photo ? (
                                <img src={alert.pet_photo} alt={alert.pet_name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-slate-300 text-slate-500">
                                    <AlertTriangle size={64} />
                                </div>
                            )}
                            <div className={`absolute top-4 left-4 text-white px-4 py-1 rounded-full font-bold text-xs shadow-lg uppercase tracking-wider ${alert.type === 'system_alert' ? 'bg-emerald-600' : 'bg-red-600'}`}>
                                {alert.type === 'system_alert' ? '📍 Ubicación Detectada' : 'SOS Activo'}
                            </div>
                        </div>

                        <div className="p-6 text-slate-900">
                            <h3 className="text-3xl font-black text-center mb-1 text-slate-800 uppercase italic tracking-tight">{alert.pet_name || 'Mascota'}</h3>
                            <p className="text-slate-500 text-center text-xs font-bold mb-4 uppercase tracking-tighter">
                                {alert.type === 'system_alert'
                                    ? 'Alguien escaneó su placa QR — ubicación en vivo disponible'
                                    : 'Visto por última vez cerca de tu zona'}
                            </p>

                            <div className="flex gap-2 mb-2">
                                <div className="flex-1 bg-slate-100 rounded-2xl p-3 text-center">
                                    <span className="block text-[8px] text-slate-400 font-bold uppercase tracking-widest mb-1">{alert.type === 'system_alert' ? 'Precisión' : 'Cercanía'}</span>
                                    <span className={`text-base font-black ${alert.type === 'system_alert' ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {alert.type === 'system_alert' ? 'GPS Real' : 'Radio 10km'}
                                    </span>
                                </div>
                                <div className="flex-1 bg-slate-100 rounded-2xl p-3 text-center">
                                    <span className="block text-[8px] text-slate-400 font-bold uppercase tracking-widest mb-1">Estado</span>
                                    <span className={`text-base font-black ${alert.type === 'system_alert' ? 'text-emerald-600' : 'text-slate-700 underline decoration-red-400 decoration-2'}`}>
                                        {alert.type === 'system_alert' ? 'Encontrado' : 'Buscando'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="w-full space-y-3">
                        {alert.type === 'new_sighting' ? (
                            <button
                                onClick={() => {
                                    closeAlert();
                                    // Custom event to tell PetFeed to open a specific alert chat
                                    localStorage.setItem('petFeedView', 'chat');
                                    localStorage.setItem('startChatAlert', JSON.stringify({ id: alert.alert_id || alert.pet_id, pet_name: alert.pet_name }));
                                    window.dispatchEvent(new CustomEvent('change-view', { detail: { view: 'PET_FEED' } }));
                                }}
                                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-black py-4 rounded-2xl shadow-xl transition-all active:scale-95 flex items-center justify-center gap-3 italic uppercase tracking-tight"
                            >
                                <MessageCircle size={24} />
                                Abrir Chat de Avistamiento
                            </button>
                        ) : (
                            <button
                                onClick={() => {
                                    // La alerta SOS trae la ubicación reportada en el push
                                    // (lat/lng) — hay que dejarla lista como "nav_dest" antes
                                    // de entrar al mapa, si no GPSMapView no tiene ningún punto
                                    // que mostrar mientras no exista un finder en vivo y termina
                                    // mostrando solo la ubicación actual del propio usuario.
                                    if (alert.lat && alert.lng) {
                                        localStorage.setItem('nav_dest', JSON.stringify({
                                            lat: parseFloat(alert.lat),
                                            lng: parseFloat(alert.lng),
                                            name: alert.pet_name || 'Mascota'
                                        }));
                                    }
                                    closeAlert();
                                    window.dispatchEvent(new CustomEvent('change-view', {
                                        detail: {
                                            view: View.GPS_TRACKING,
                                            id: alert.pet_id,
                                            qr_scan: 'true'
                                        }
                                    }));
                                }}
                                className="w-full bg-red-600 hover:bg-red-700 text-white font-black py-4 rounded-2xl shadow-xl transition-all active:scale-95 flex items-center justify-center gap-3 italic uppercase tracking-tight"
                            >
                                <Map size={24} />
                                Rastrear en Tiempo Real
                            </button>
                        )}

                        <button
                            onClick={closeAlert}
                            className="w-full bg-slate-800 text-white font-black py-4 rounded-2xl transition-all active:scale-95 border-2 border-slate-700 flex items-center justify-center gap-3 uppercase tracking-tight"
                        >
                            <X size={20} />
                            Descartar
                        </button>

                        <button onClick={closeAlert} className="w-full py-2 text-slate-400 font-bold hover:text-white transition-colors text-sm uppercase">Cerrar Alerta</button>
                    </div>
                </div>
            </div>
        );
    }

    if (level === 2) {
        return (
            <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in">
                <div className="bg-white rounded-[2rem] shadow-2xl max-w-sm w-full border-t-[16px] border-red-500 overflow-hidden animate-in slide-in-from-bottom-10">
                    <div className="bg-red-50 p-6 flex flex-col items-center text-center border-b border-red-100">
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center text-red-600 mb-4 shadow-inner">
                            <AlertTriangle size={40} />
                        </div>
                        <h2 className="text-2xl font-black text-red-600 uppercase italic tracking-tighter">Atención de Sistema</h2>
                    </div>
                    <div className="p-8 text-center space-y-4">
                        <p className="text-slate-600 font-bold text-lg leading-tight">{alert.body || "Error de sistema detectado."}</p>

                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(alert.body || "");
                                // Feedback visual simple
                                const btn = document.getElementById('btn-copy-alert');
                                if (btn) btn.innerText = "¡Copiado!";
                                setTimeout(() => { if (btn) btn.innerText = "Copiar Mensaje"; }, 2000);
                            }}
                            id="btn-copy-alert"
                            className="w-full bg-slate-100 text-slate-600 py-3 rounded-xl font-black uppercase flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors"
                        >
                            <Copy size={16} /> Copiar Mensaje
                        </button>

                        <button onClick={closeAlert} className="w-full bg-red-500 text-white py-3 rounded-xl font-black uppercase shadow-lg hover:bg-red-600 transition-colors italic">Entendido</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed bottom-0 left-0 right-0 z-[100000] p-4 flex justify-center pointer-events-none mb-20">
            <div className="bg-amber-400 text-amber-950 pointer-events-auto rounded-3xl shadow-2xl p-4 max-w-md w-full flex items-center gap-4 animate-in slide-in-from-bottom-20 bounce-in border-4 border-white group">
                <div className="bg-white/20 p-2 rounded-xl shrink-0"><ShieldCheck size={24} /></div>
                <div className="flex-1">
                    <h4 className="font-black uppercase text-[10px] tracking-widest opacity-70 italic">Amigo Peludo</h4>
                    <p className="font-bold text-sm tracking-tight">{alert.body || "Recordatorio pendiente."}</p>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => navigator.clipboard.writeText(alert.body || "")}
                        className="p-2 hover:bg-black/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                        title="Copiar recordatorio"
                    >
                        <Copy size={18} />
                    </button>
                    <button onClick={closeAlert} className="p-2 hover:bg-black/10 rounded-lg transition-colors"><X size={20} /></button>
                </div>
            </div>
        </div>
    );
};
